import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { removeAppLogo, uploadAppLogo, type UserAppDraft } from '@api/appBuilder';
import { axiosErrorMessage } from '@api/errors';
import useSnackbar from '@hooks/useSnackbar';
import { appBuilderKeys } from '../useAppBuilder';
import { LogoError, prepareLogo } from './appLogo';

export const LOGO_MESSAGES = {
  format: { id: 'apps.builder.logo.format', defaultMessage: 'Use a PNG or SVG logo of 512 KB or less.' },
  shape: {
    id: 'apps.builder.logo.shape',
    defaultMessage: 'Use a logo at least 64 px tall, square or up to six times wider than tall.',
  },
  failed: { id: 'apps.builder.logo.failed', defaultMessage: 'The logo could not be saved. Try again.' },
} as const;

export const useAppLogo = (appKey: string) => {
  const intl = useIntl();
  const { showError } = useSnackbar();
  const queryClient = useQueryClient();
  const landed = (draft: UserAppDraft) => queryClient.setQueryData(appBuilderKeys.draft(draft.appKey), draft);
  const failed = (err: unknown) =>
    showError(
      err instanceof LogoError
        ? intl.formatMessage(LOGO_MESSAGES[err.problem])
        : axiosErrorMessage(err, intl.formatMessage(LOGO_MESSAGES.failed)),
    );

  const upload = useMutation({
    mutationFn: async (file: File) => uploadAppLogo(appKey, await prepareLogo(file)),
    onSuccess: landed,
    onError: failed,
  });
  const remove = useMutation({
    mutationFn: () => removeAppLogo(appKey),
    onSuccess: landed,
    onError: failed,
  });

  return {
    upload: (file: File) => upload.mutate(file),
    remove: () => remove.mutate(),
    busy: upload.isPending || remove.isPending,
  };
};
